import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconPitstop } from '@/assets/svgs/icon-pitstop.svg';
import { featureConstant } from "@/constants/configuration/feature";
import { feature } from "@/utils/feature-verifier";
import { produce } from 'immer';
import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import Loader from 'react-loader-spinner';
import { Checkbox, Radio } from '@dextech-ai/dex-design-system';

const NoMessage = ({ text = "No principal added yet" }) => {
	return (
		<div className="data-table__empty__icon">
			<span>{text}</span>
		</div>
	);
};

NoMessage.propTypes = {
	text: PropTypes.string,
}

// The header and the rows are click targets themselves (sort / select). Their
// handlers skip events that come from the checkbox, which toggles on its own.
const isCheckboxEvent = (e) => !!e?.target?.closest?.('.ant-checkbox-wrapper');

const PrincipalTableList = ({ principals, setPrincipals, setCurrentParticipants, searchPrincipal, pagination, orderBySelectedKey = false, titleHeader = 'Principals', inputType, loader, emptyText = "No principal added yet", emptySearchText = "No principal found", selectedKey = 'enrolmentId' }) => {

	/*=================================================
		Paginate
	/================================================*/

	const [currentPage, setCurrentPage] = useState(1)
	const totalPerPage = 30
	const [totalPage, setTotalPage] = useState()
	const [totalItemCount, setTotalItemCount] = useState('')
	const [principalList, setPrincipalList] = useState([])
	const [displayPrincipalList, setDisplayPrincipalList] = useState([])

	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2);

	function getPageStart(pageSize, pageNo) {
		return pageSize * pageNo
	}

	function getPageLabel(total, pageSize, pageNo) {
		if (principalList?.length === 0) {
			return "0"
		}

		const start = Math.max(getPageStart(pageSize, pageNo), 0)
		const end = Math.min(getPageStart(pageSize, pageNo + 1), total)

		return `${start + 1} - ${end}`
	}

	function handleCurrentInput(e) {
		const value = e.target?.value === '' ? 1 : e.target.value
		const current = parseInt(value)

		if (current < 1) {
			return setCurrentPage(1)
		}

		if (current > totalPage) {
			return setCurrentPage(totalPage)
		}

		setCurrentPage(current)
	}

	function goToPrev() {
		if (currentPage === 1) {
			return
		}

		setCurrentPage((prev) => prev - 1)
	}

	function goToNext() {
		if (currentPage === totalPage) {
			return;
		}
		setCurrentPage((prev) => prev + 1)
	}

	useEffect(() => {
		if (currentPage > totalPage) {
			setCurrentPage(1)
		}
	}, [principalList])

	/*=================================================
		Sort
	/================================================*/

	const [sortConfig, setSortConfig] = useState({ sortBy: "", direction: true });

	function handleSort(sortBy) {
		let direction = sortConfig.direction;
		if (sortConfig.sortBy === sortBy) {
			direction = !direction;
		} else {
			direction = true;
		}

		setSortConfig({ sortBy, direction });
	}

	function getClassName(name) {
		if (!sortConfig) {
			return;
		}

		const sortDirection = sortConfig.direction ? "is-asc" : "is-desc"

		return sortConfig.sortBy === name ? sortDirection : "";
	}


	useEffect(() => {
		if (principals?.length === 0) {
			return
		}

		const start = currentPage > 1 ? (currentPage - 1) * totalPerPage : 0
		let updatedParticipantList = principals ? [...principals] : [];

		if (searchPrincipal?.trim()?.length > 0) {
			updatedParticipantList = updatedParticipantList.filter((principal) => {
				if (isAgentPrincipalPhase2Enabled && titleHeader !== 'Principals') {
					return (
						principal?.organization?.name.toLowerCase().includes(searchPrincipal.trim().toLowerCase())
					);
				} else {
					return (
						principal.name.toLowerCase().includes(searchPrincipal.trim().toLowerCase()) ||
						principal.uen.toLowerCase().includes(searchPrincipal.trim().toLowerCase())
					);
				}
			});
		}

		if (sortConfig.sortBy?.length) {
			updatedParticipantList?.sort((a, b) => {
				if (orderBySelectedKey) {
					if (b?.[selectedKey]) return 1
					else if (a?.[selectedKey]) return -1
				}
				return (a?.[sortConfig?.sortBy] > b?.[sortConfig?.sortBy] ? 1 : -1) * (sortConfig?.direction ? 1 : -1)
			})
		}

		setPrincipalList(updatedParticipantList)
		setTotalPage(Math.ceil(updatedParticipantList.length / totalPerPage))
		setTotalItemCount(updatedParticipantList.length)
		const currentParticipantList = updatedParticipantList.slice(start, start + totalPerPage)
		setDisplayPrincipalList(pagination === false ? updatedParticipantList : currentParticipantList)
	}, [currentPage, searchPrincipal, principals, sortConfig])

	const isAllChecked = (itemData) => {
		return itemData?.length && itemData?.every((item) => item?.$selected === true || item?.$disabled === true) && itemData?.some((item) => item?.$selected === true);
	};

	const handleUpdatePrincipalSelect = (item) => {
		if (item?.$disabled) return

		const index = principals?.findIndex(principal => principal && principal.id === item?.id);

		if (inputType === 'radio') {
			const updatedItems = produce(principals, draft => {
				draft.forEach(
					principals => principals.$selected = false
				)
				draft[index].$selected = !draft[index].$selected
			})
			setCurrentParticipants(updatedItems[index])
			setPrincipals(updatedItems)
		} else {
			const updatedItems = produce(principals, draft => {
				draft[index].$selected = !draft[index].$selected
			})
			setPrincipals(updatedItems)
		}
	}

	const handleUpdateAllSelect = (checked) => {
		if (principals?.length) {
			const updatedItems = produce(principals, draft => {
				draft?.map(s => {
					if (displayPrincipalList?.some(item => item.id === s.id)) {
						s['$selected'] = checked
					}
					return s
				})
			})
			setPrincipals(updatedItems)
		}
	}

	return (
		<div
			data-testid="principal-table__table"
			className="principal-table__table">
			<div className="principal-table__table__head__row">
				<div
					className={`principal-table__header__text title__grey data-table__table__head__title__sort-order link ${getClassName(
						"name"
					)}`}
					id="principals"
					onClick={(e) => {
						if (isCheckboxEvent(e)) return;
						handleSort("name");
					}}
				>
					{inputType === 'radio' ?
						<></>
						:
						<Checkbox
							className="principal-table__checkbox"
							data-testid="principal-checkbox"
							checked={!!isAllChecked(displayPrincipalList)}
							disabled={displayPrincipalList?.every(s => s?.$disabled) ?? false}
							indeterminate={!isAllChecked(displayPrincipalList) && displayPrincipalList?.some(item => item?.$selected)}
							onCheckedChange={(checked) =>
								handleUpdateAllSelect(checked)
							}
						/>
					}
					{isAgentPrincipalPhase2Enabled && titleHeader !== "Principals" ? "Participants" : titleHeader}{" "}
					<span className="data-table__table__head__icon__sort-order"></span>
				</div>
			</div>
			{displayPrincipalList?.length > 0 ? (
				<>
					{displayPrincipalList?.map((item, index) => {
						return (
							<div
								className="principal_table__body__rows"
								key={item?.id ?? index}
								onClick={(e) => isCheckboxEvent(e) || handleUpdatePrincipalSelect(item)}
								onKeyPress={(e) => isCheckboxEvent(e) || handleUpdatePrincipalSelect(item)}
								tabIndex={0}
							>
								{inputType === 'radio' ?
									// The row is the click target; the radio only shows its state.
									<span className="principal-table__radio" aria-hidden="true">
										<Radio
											value={item?.id ?? index}
											data-testid="role-checkbox"
											checked={!!item?.$selected}
											disabled={!!item?.$disabled}
											tabIndex={-1}
										/>
									</span>
									:
									<Checkbox
										className="principal-table__checkbox"
										data-testid="role-checkbox"
										checked={!!item?.$selected}
										disabled={!!item?.$disabled}
										onCheckedChange={() =>
											handleUpdatePrincipalSelect(item)
										}
									/>
								}
								<div className="principal_table_data__row">
									<div
										className="layout-request__tile__content__avatar"
										style={{
											backgroundImage: `url(${item?.organisation?.iconUrl})`,
										}}
									>
										{!item?.organisation?.iconUrl && <IconPitstop />}
									</div>
									<div className="principal_data__col__org_name_uen">
										{(isAgentPrincipalPhase2Enabled && titleHeader !== 'Principals') || (!isAgentPrincipalPhase2Enabled && titleHeader === 'Contributors') ?
											<div data-testid="item-name">{item?.organization?.name}</div>
											:
											<>
												<div data-testid="item-name">{item?.name}</div>
												<div>
													UEN: {item?.uen}
												</div>
											</>
										}
									</div>
								</div>
							</div>
						);
					})}
				</>
			) : (
				<div className="data-table__empty">
					{loader ? (
						<Loader
							className="loadingSpinner__position__center"
							data-testid="loadingSpinner__position__center"
							type="ThreeDots"
							color="grey"
							height={10}
							width={15}
						/>
					) : (
						<NoMessage text={principals?.length && searchPrincipal?.length ? emptySearchText : emptyText} />
					)
					}
				</div>
			)}
			{pagination && (
				<div className="data-table__table__footer">
					<div className="data-table__table__footer__row">
						<div className="data-table__table__footer__row__left">
							<div className="data-table__table__pagination">
								<input
									data-testid="pagination-input"
									className="data-table__table__pagination__page"
									type="number"
									value={currentPage}
									onChange={(e) => handleCurrentInput(e)}
									min="1"
								/>
								<span className="data-table__table__pagination__page__meta">
									{' '}
									of {totalPage || 1} pages{' '}
								</span>
								<button
									data-testid="pagination-prev"
									className="data-table__table__pagination__prev"
									onClick={() => goToPrev()}
									disabled={currentPage === 1}
								>
									<IconChevronDown />
								</button>
								<button
									data-testid="pagination-next"
									className="data-table__table__pagination__next"
									onClick={() => goToNext()}
									disabled={!totalPage || currentPage === totalPage}
								>
									<IconChevronDown />
								</button>
							</div>
						</div>
						<div className="data-table__table__footer__row__right">
							<div className="data-table__table__meta">
								Showing{' '}
								{getPageLabel(
									totalItemCount,
									totalPerPage,
									currentPage - 1,
								)}{' '}
								of{' '}
								{principalList.length === 0 && searchPrincipal.length > 0
									? principals.length
									: totalItemCount || 0}{' '}
								entries
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	)
};

PrincipalTableList.propTypes = {
	principals: PropTypes.object,
	setCurrentParticipants: PropTypes.func,
	searchPrincipal: PropTypes.string,
	pagination: PropTypes.bool,
	orderBySelectedKey: PropTypes.bool,
	titleHeader: PropTypes.string,
	inputType: PropTypes.oneOf(['checkbox', 'radio']),
	loader: PropTypes.bool,
	emptyText: PropTypes.string,
	emptySearchText: PropTypes.string,
	selectedKey: PropTypes.string,
};

export default PrincipalTableList;
